import React, { useState, useMemo, useContext } from "react";
import { FormFactory } from "@/modules/lead-page/components/lead-info/edit-fields/status/forms/form-factory/form-factory";
import BlockedForm from "@/modules/lead-page/components/lead-info/edit-fields/status/forms/static-forms/blocked-form";
import ReserveForm from "@/modules/lead-page/components/lead-info/edit-fields/status/forms/static-forms/reserve-form";
import SaleForm from "@/modules/lead-page/components/lead-info/edit-fields/status/forms/static-forms/sale-form";
import PromiseSaleForm from "@/modules/lead-page/components/lead-info/edit-fields/status/forms/static-forms/promise-sale-form";
import ExpoMeetingForm from "@/modules/lead-page/components/lead-info/edit-fields/status/forms/static-forms/expo-meeting-form";
import {
  FormComponentType,
  FormFactoryComponent,
  FormRendererProps,
  SaleType,
  StaticFormComponent,
} from "@/modules/lead-page/types";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Box,
  CircularProgress,
} from "@mui/material";
import { queryCommonFlags } from "@/api/constants";
import { useQuery } from "@tanstack/react-query";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import { SalesResponseV2 } from "@/modules/sales/dtos";
import { SalesStatuses, SaleTypeNames } from "@/modules/sales/constants";
import { isSameStatus } from "@/modules/lead-page/utils/status-name";
import { getLeadSales } from "@/modules/leads/api/index.v2";
import { EnvironmentVariables } from "@/modules/common/types";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import ErealtyIframeModal from "@/modules/lead-page/components/lead-info/edit-fields/status/erealty-iframe-modal";

const formComponents: { [key in SaleType | "Default"]: FormComponentType } = {
  [SaleType.Reserved]: {
    type: "static",
    component: ReserveForm as StaticFormComponent,
  },
  [SaleType.Contracted]: {
    type: "static",
    component: SaleForm as StaticFormComponent,
  },
  [SaleType.Blocked]: {
    type: "static",
    component: BlockedForm as StaticFormComponent,
  },
  [SaleType.EOI]: {
    type: "static",
    component: BlockedForm as StaticFormComponent,
  },
  [SaleType.PromiseSale]: {
    type: "static",
    component: PromiseSaleForm as StaticFormComponent,
  },
  Default: { type: "factory", component: FormFactory as FormFactoryComponent },
};

const NON_SALE_STATUS_FORMS = {
  MEETING_HAPPENED_EXPO: "Meeting happened - Expo",
} as const;

const isExpoMeetingStatus = (statusName: string): boolean => {
  return statusName
    .toLowerCase()
    .includes(NON_SALE_STATUS_FORMS.MEETING_HAPPENED_EXPO.toLowerCase());
};

const FormRenderer: React.FC<FormRendererProps> = ({
  statusName,
  selectedStatus,
  handleSubmit,
  isLoadingSubmition,
  handleClose,
  formFields,
  getSaleType,
  handleDeleteFile,
  handleFiles,
  files,
  paymentFiles,
  paymentPlanFiles,
  handlePaymentFiles,
  handlePaymentPlanFiles,
  handleDeletePaymentFiles,
  erealtyIframeOpenCommentModal,
  setErealtyIframeOpenCommentModal,
  erealtyIframeComment,
  setErealtyIframeComment,
  handleErealtyIframeConfirm,
  erealtyIframePayload,
  createdTracks,
  developers,
  isLoadingDevelopers,
  setSearchDevelopers,
  handleAddTrack,
  handleDeleteTrack,
  handleTrackChange,
}) => {
  const { lead } = useContext(LeadPageContext);

  const baseSaleType = getSaleType(statusName) as SaleType | null;

  // Check if Promise form should be shown instead of Contracted form
  const leadTypeName = lead?.data?.lead_type?.lead_type || "";
  const isSellerOrResaleBuyer =
    leadTypeName.toLowerCase().includes("seller") ||
    leadTypeName.toLowerCase().includes("resale buyer");

  const shouldShowPromiseForm =
    isSameStatus(statusName, SalesStatuses.PROMISE_SALE) ||
    (isSameStatus(statusName, SalesStatuses.CONGRATS_ITS_A_SALE) &&
      isSellerOrResaleBuyer);

  // Override sale type to Promise if conditions are met
  const saleType =
    shouldShowPromiseForm &&
    isSameStatus(statusName, SalesStatuses.CONGRATS_ITS_A_SALE)
      ? SaleType.PromiseSale
      : baseSaleType;

  const {
    data: LeadSalesData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["getLeadSales"],
    queryFn: () => getLeadSales(lead?.data.lead.uid),
    ...queryCommonFlags,
  });

  const { isMobile } = useScreenSize();

  const erealtyFiltersUrl = `${EnvironmentVariables.EREALTY_URL}/crm/projects?isMobile=${isMobile}`;

  const leadSales = useMemo(
    () => (Array.isArray(LeadSalesData?.data) ? LeadSalesData.data : []),
    [LeadSalesData],
  );

  const [selectedSale, setSelectedSale] = useState<string>("new");

  const handleSaleChange = (event: SelectChangeEvent) => {
    setSelectedSale(event.target.value as string);
  };

  const eligibleSalesTypes = useMemo(() => {
    const shouldIncludePromise = shouldShowPromiseForm;

    if (
      saleType === SaleType.Contracted ||
      (isSameStatus(statusName, SalesStatuses.CONGRATS_ITS_A_SALE) &&
        isSellerOrResaleBuyer)
    ) {
      const contractedTypes = [
        SaleTypeNames.Contracted,
        SaleTypeNames.Reserved,
        SaleTypeNames.Blocked,
        SaleTypeNames.EOI,
        SaleTypeNames.Potential,
      ];
      return shouldIncludePromise
        ? [...contractedTypes, SaleTypeNames.Promise]
        : contractedTypes;
    } else if (saleType === SaleType.Reserved) {
      const reservedTypes = [
        SaleTypeNames.Reserved,
        SaleTypeNames.Blocked,
        SaleTypeNames.EOI,
        SaleTypeNames.Potential,
      ];
      return shouldIncludePromise
        ? [...reservedTypes, SaleTypeNames.Promise]
        : reservedTypes;
    } else if (saleType === SaleType.Blocked) {
      const blockedTypes = [SaleTypeNames.Blocked, SaleTypeNames.Potential];
      return shouldIncludePromise
        ? [...blockedTypes, SaleTypeNames.Promise]
        : blockedTypes;
    } else if (saleType === SaleType.EOI) {
      const eoiTypes = [SaleTypeNames.EOI, SaleTypeNames.Potential];
      return shouldIncludePromise
        ? [...eoiTypes, SaleTypeNames.Promise]
        : eoiTypes;
    } else if (saleType === SaleType.PromiseSale) {
      return [SaleTypeNames.Promise];
    }

    // For other cases, check if Promise should be included as standalone
    return shouldIncludePromise ? [SaleTypeNames.Promise] : [];
  }, [saleType, shouldShowPromiseForm]);

  const filteredSales = useMemo(
    () =>
      leadSales.filter((sale: SalesResponseV2) =>
        eligibleSalesTypes.includes(sale?.saleType?.name as SaleTypeNames),
      ),
    [leadSales, eligibleSalesTypes],
  );

  // Component rendering logic outside the hooks
  const renderSalesDropdown = () => (
    <FormControl fullWidth>
      <InputLabel id="sale-select-label">Select a sale</InputLabel>
      <Select
        labelId="sale-select-label"
        id="sale-select"
        value={selectedSale}
        label="Select a sale"
        onChange={handleSaleChange}
      >
        <MenuItem value="new">New Sale</MenuItem>
        {filteredSales.map((sale: SalesResponseV2) => (
          <MenuItem key={sale.uid} value={sale.uid}>
            {sale?.saleType?.name} -{" "}
            {sale.saleType.name === SaleType.Contracted
              ? sale.unitNumber
              : sale.compound?.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // Handle non-sale status forms
  if (statusName && isExpoMeetingStatus(statusName)) {
    return (
      <ExpoMeetingForm
        handleSaleSubmission={handleSubmit}
        handleClose={handleClose}
        isLoadingSubmition={isLoadingSubmition}
      />
    );
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          width: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return <div>Error loading sales. Please try again.</div>;
  }

  const selectedSaleObject =
    leadSales.find((sale: SalesResponseV2) => sale.uid === selectedSale) ||
    null;

  const FormComponentType = saleType
    ? formComponents[saleType]
    : formComponents.Default;
  if (statusName) {
    if (FormComponentType.type === "factory") {
      if (selectedStatus.erealtyForm) {
        return (
          <ErealtyIframeModal
            open={true}
            erealtyFiltersUrl={erealtyFiltersUrl}
            handleClose={handleClose}
            erealtyIframeOpenCommentModal={erealtyIframeOpenCommentModal}
            setErealtyIframeOpenCommentModal={setErealtyIframeOpenCommentModal}
            erealtyIframeComment={erealtyIframeComment}
            setErealtyIframeComment={setErealtyIframeComment}
            handleErealtyIframeConfirm={handleErealtyIframeConfirm}
            isLoadingSubmition={isLoadingSubmition}
            erealtyIframePayload={erealtyIframePayload}
          />
        );
      } else {
        const FactoryComponent =
          FormComponentType.component as FormFactoryComponent;
        return (
          <FactoryComponent
            submitFun={handleSubmit}
            formFields={formFields?.data}
            selectedStatus={selectedStatus}
            handleClose={handleClose}
            isLoadingSubmition={isLoadingSubmition}
            developerTrackConfig={{
              createdTracks: createdTracks || [],
              developers: developers || [],
              isLoadingDevelopers: isLoadingDevelopers || false,
              setSearchDevelopers: setSearchDevelopers || (() => {}),
              handleAddTrack: handleAddTrack || (() => {}),
              handleDeleteTrack: handleDeleteTrack || (() => {}),
              handleTrackChange: handleTrackChange || (() => {}),
            }}
          />
        );
      }
    } else {
      return (
        <>
          {renderSalesDropdown()}
          <FormComponentType.component
            handleSaleSubmission={handleSubmit}
            isLoadingSubmition={isLoadingSubmition}
            handleClose={handleClose}
            saleType={saleType}
            sale={selectedSaleObject}
            files={files}
            handleFiles={handleFiles}
            paymentFiles={paymentFiles}
            paymentPlanFiles={paymentPlanFiles}
            handlePaymentFiles={handlePaymentFiles}
            handlePaymentPlanFiles={handlePaymentPlanFiles}
            handleDeletePaymentFiles={handleDeletePaymentFiles}
            handleDeleteFile={handleDeleteFile}
            statusName={statusName}
          />
        </>
      );
    }
  }
};

export default FormRenderer;
